import { cn } from '@/lib/utils';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import './input-component.css';

interface InputProps {
  name: string;
  label: string;
  type?: string;
  rules: Object;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors: any;
  className?: string;
  submitError?: string | null;
  setSubmitError?: Dispatch<
    SetStateAction<{
      message: string;
    } | null>
  >;
  visibilityToggle?: boolean;
}

const InputComponent = ({
  name,
  label,
  type = 'text',
  rules,
  register,
  setValue,
  errors,
  className,
  submitError,
  setSubmitError,
  visibilityToggle,
}: InputProps) => {
  const { onChange, onBlur, ...inputProps } = register(name, rules);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const labelClassName =
    'absolute text-off-black duration-300 transform -translate-y-3 scale-75 top-4 origin-[0] start-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto pointer-events-none';

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setSubmitError) {
      setSubmitError(null);
    }
    setValue(name, e.target.value, { shouldValidate: true });
  };

  return (
    <div className={`relative text-black/90 font-medium`}>
      <input
        id={name}
        type={isPasswordVisible ? 'text' : type}
        className={cn(
          'peer block bg-white px-5 pt-5 pb-2.5 rounded-full outline outline-2 focus-visible:outline-black w-full appearance-none',
          errors[name] || submitError
            ? 'outline-red-500 focus-visible:outline-red-500'
            : 'outline-gray',
          className,
          isPasswordVisible && '!font-proxima_font',
          visibilityToggle && 'pr-14'
        )}
        placeholder=" "
        {...inputProps}
        onBlur={(e) => {
          onBlur(e);
          setValue(name, e.target.value, { shouldValidate: true });
        }}
        onChange={handleOnChange}
        // role="presentation"
        // autoComplete="new-password"
      />
      <label htmlFor={name} className={labelClassName}>
        {label}
      </label>
      {errors[name] && (
        <p className="flex items-center gap-1 mt-1 font-medium text-red-500 text-xs">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="red"
            viewBox="0 0 256 256"
            className="shrink-0"
          >
            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
          </svg>

          {errors[name].message}
        </p>
      )}
      {submitError && (
        <p className="flex items-center gap-1 mt-1 font-medium text-red-500 text-xs">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="red"
            viewBox="0 0 256 256"
            className="shrink-0"
          >
            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
          </svg>

          {submitError}
        </p>
      )}
      {visibilityToggle && (
        <button
          type="button"
          className="top-4 right-5 absolute flex items-center text-off-black origin-[0]"
          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
        >
          {isPasswordVisible ? (
            <Eye className="w-5 h-5" />
          ) : (
            <EyeSlash className="w-5 h-5" />
          )}
        </button>
      )}
    </div>
  );
};

export default InputComponent;
